import React, { useEffect, useState } from "react"
import { Button, Checkbox, Modal } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUsers } from "@fortawesome/free-solid-svg-icons"

import { useProjectMembersQuery } from "features/Projects/api/useProjectsQuery"
import {
  useAddProjectMemberMutation,
  useRemoveProjectMemberMutation,
} from "features/Projects/api/useProjectsMutations"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"

import { useProjectStore } from "stores/projectStore"

interface ProjectMembersModalProps {
  userData: string[]
}

/**
 * Modal component for managing project members.
 *
 * This component displays a list of users and allows adding/removing them as project members.
 *
 * @param props - The component props
 * @param props.userData - Array of available users in the unit
 * @returns The rendered project members modal component.
 */
const ProjectMembersModal: React.FC<ProjectMembersModalProps> = ({ userData }) => {
  const project = useProjectStore((state) => state.project)

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [selectedMembers, setSelectedMembers] = useState<string[]>([])
  const [displayUserData, setDisplayUserData] = useState<string[]>([])

  // Loads when the modal opens; add/remove mutations invalidate the key.
  const membersQuery = useProjectMembersQuery(project.id, isModalVisible)
  const projectMembers = membersQuery.data ?? []
  const addMemberMutation = useAddProjectMemberMutation()
  const removeMemberMutation = useRemoveProjectMemberMutation()

  // Prepare user data with project owner first
  useEffect(() => {
    if (userData.length > 0) {
      const userArray = [
        project.Created_by,
        ...userData.filter(
          (user: string) =>
            user.toLowerCase() !== project.Created_by.toLowerCase()
        ),
      ]
      setDisplayUserData(userArray)
    }
  }, [userData, project.Created_by])

  /**
   * Adds selected members to the project. Notifications are handled by the
   * mutation; a failure for one member does not stop the rest.
   * @async
   * @function addMember
   * @returns {Promise<void>} A promise that resolves when all selected members are processed.
   */
  const addMember = async (): Promise<void> => {
    for (const member of selectedMembers) {
      if (projectMembers.includes(member)) {
        notify.error(
          "Member Already Exists!",
          `Member ${member} is already a part of the project.`
        )
        continue
      }

      try {
        await addMemberMutation.mutateAsync({ id: project.id, member })
      } catch (error) {
        console.error("Error Adding Members:", (error as Error).message)
      }
    }
    // Clear selected members after processing
    setSelectedMembers([])
  }

  /**
   * Removes a member from the project. Notifications and the member-list
   * refresh are handled by the mutation.
   * @async
   * @function removeMember
   * @param {string} memberName - The name of the member to remove.
   * @returns {Promise<void>} A promise that resolves when the member is removed.
   */
  const removeMember = async (memberName: string): Promise<void> => {
    try {
      await removeMemberMutation.mutateAsync({
        id: project.id,
        member: memberName,
      })
    } catch (error) {
      console.error("Error Removing Member:", (error as Error).message)
    }
  }

  /**
   * Shows the modal.
   * @function showModal
   */
  const showModal = (): void => {
    setIsModalVisible(true)
  }

  /**
   * Hides the modal.
   * @function cancel
   */
  const cancel = (): void => {
    setIsModalVisible(false)
    // Clear selected members when canceling
    setSelectedMembers([])
  }

  /**
   * Handles the selection of a project member.
   *
   * @function handleMemberSelect
   * @param {string} selectedMember - The selected member string.
   * @returns {Function} The event handler function.
   */
  const handleMemberSelect = (selectedMember: string) => (): void => {
    // Prevent the project creator from being added to the selected members
    if (selectedMember === project.Created_by) {
      return
    }

    setSelectedMembers((prevSelected: string[]) =>
      prevSelected.includes(selectedMember)
        ? prevSelected.filter((member: string) => member !== selectedMember)
        : [...prevSelected, selectedMember]
    )
  }

  const selectedCount = selectedMembers.length
  const addLabel =
    selectedCount === 0
      ? "Add members"
      : `Add ${selectedCount} member${selectedCount > 1 ? "s" : ""}`

  return (
    <>
      <DataQButton onClick={showModal} type="secondary" size="middle">
        <FontAwesomeIcon icon={faUsers} />
        Members
      </DataQButton>
      <Modal
        open={isModalVisible}
        centered={true}
        title="Project members"
        onCancel={cancel}
        destroyOnHidden={true}
        footer={
          <>
            <DataQButton type="secondary" onClick={cancel}>
              Cancel
            </DataQButton>
            <DataQButton
              onClick={addMember}
              type="primary"
              isDisabled={selectedCount === 0}
            >
              {addLabel}
            </DataQButton>
          </>
        }
      >
        <p className="mt-0 mb-2 text-gray-500">
          Tick the people to add to this project.
        </p>
        {/* One row per person in the unit, the owner first: members are
            ticked (and can be removed), everyone else can be ticked to add. */}
        <ul
          aria-label="People"
          className="m-0 max-h-80 list-none overflow-y-auto p-0"
        >
          {displayUserData.map((member: string) => {
            const isProjectOwner: boolean = project.Created_by === member
            const isMember: boolean =
              isProjectOwner || projectMembers.includes(member)
            const isRemoving: boolean =
              removeMemberMutation.isPending &&
              removeMemberMutation.variables?.member === member

            return (
              <li
                key={member}
                className="flex min-h-9 items-center gap-2 border-b border-gray-100 py-1 last:border-b-0"
              >
                <Checkbox
                  checked={isMember || selectedMembers.includes(member)}
                  disabled={isMember}
                  onChange={handleMemberSelect(member)}
                >
                  <span className="text-gray-900">{member}</span>
                </Checkbox>
                {isProjectOwner ? (
                  <span className="ml-auto text-xs text-gray-500">Owner</span>
                ) : (
                  isMember && (
                    <Button
                      className="ml-auto"
                      type="link"
                      danger
                      size="small"
                      loading={isRemoving}
                      onClick={() => void removeMember(member)}
                      aria-label={`Remove ${member}`}
                    >
                      Remove
                    </Button>
                  )
                )}
              </li>
            )
          })}
        </ul>
      </Modal>
    </>
  )
}

export default ProjectMembersModal
